/* ==========================================================================
   IAMONEAI - Environment Configuration
   Handles API URLs, keys, and environment-specific settings
   ========================================================================== */

// ==========================================================================
// ENVIRONMENT DETECTION
// ==========================================================================

/**
 * Detect current environment based on hostname
 * @returns {'development' | 'staging' | 'production'}
 */
const detectEnvironment = () => {
  if (typeof window === 'undefined') {
    return 'development';
  }
  
  const hostname = window.location.hostname;
  
  // Production domains
  if (
    hostname === 'app.iamoneai.com' ||
    hostname === 'iamoneai.com' ||
    hostname === 'www.iamoneai.com' ||
    hostname.endsWith('.pages.dev') // Cloudflare Pages
  ) {
    return 'production';
  }
  
  // Staging domains
  if (
    hostname.includes('staging') ||
    hostname.includes('preview')
  ) {
    return 'staging';
  }
  
  // Development (localhost, 127.0.0.1, etc.)
  return 'development';
};

export const ENVIRONMENT = detectEnvironment();
export const IS_DEVELOPMENT = ENVIRONMENT === 'development';
export const IS_STAGING = ENVIRONMENT === 'staging';
export const IS_PRODUCTION = ENVIRONMENT === 'production';


// ==========================================================================
// ENVIRONMENT-SPECIFIC CONFIGURATION
// ==========================================================================

const config = {
  development: {
    API_URL: 'https://api.iamoneai.com',
    API_KEY: 'hpXaxXVSYu7lyC3XkpHx35wl5ZagFCv9wXY1eggi',
    APP_URL: 'http://localhost:5173',
    
    // Feature flags
    ENABLE_DEBUG_LOGGING: true,
    ENABLE_MOCK_DATA: false,
    ENABLE_DEV_TOOLS: true,
    
    // Analytics
    ENABLE_ANALYTICS: false,
    
    // reCAPTCHA (use test keys in dev if needed)
    RECAPTCHA_SITE_KEY: '6Lc0jCEsAAAAAPjsIGdEtF7UqO6e7tdzP5JnK_AS',
  },
  
  staging: {
    API_URL: 'https://api.iamoneai.com',
    API_KEY: 'hpXaxXVSYu7lyC3XkpHx35wl5ZagFCv9wXY1eggi',
    APP_URL: 'https://staging.iamoneai.com',
    
    // Feature flags
    ENABLE_DEBUG_LOGGING: true,
    ENABLE_MOCK_DATA: false,
    ENABLE_DEV_TOOLS: true,
    
    // Analytics
    ENABLE_ANALYTICS: false,
    
    // reCAPTCHA
    RECAPTCHA_SITE_KEY: '6Lc0jCEsAAAAAPjsIGdEtF7UqO6e7tdzP5JnK_AS',
  },
  
  production: {
    API_URL: 'https://api.iamoneai.com',
    API_KEY: 'hpXaxXVSYu7lyC3XkpHx35wl5ZagFCv9wXY1eggi',
    APP_URL: 'https://app.iamoneai.com',
    
    // Feature flags
    ENABLE_DEBUG_LOGGING: false,
    ENABLE_MOCK_DATA: false,
    ENABLE_DEV_TOOLS: false,
    
    // Analytics
    ENABLE_ANALYTICS: true,
    
    // reCAPTCHA
    RECAPTCHA_SITE_KEY: '6Lc0jCEsAAAAAPjsIGdEtF7UqO6e7tdzP5JnK_AS',
  },
};

// Get current environment config
const currentConfig = config[ENVIRONMENT];


// ==========================================================================
// EXPORTED CONFIGURATION
// ==========================================================================

// API Configuration
export const API_URL = currentConfig.API_URL;
export const API_KEY = currentConfig.API_KEY;
export const APP_URL = currentConfig.APP_URL;

// Feature Flags
export const ENABLE_DEBUG_LOGGING = currentConfig.ENABLE_DEBUG_LOGGING;
export const ENABLE_MOCK_DATA = currentConfig.ENABLE_MOCK_DATA;
export const ENABLE_DEV_TOOLS = currentConfig.ENABLE_DEV_TOOLS;
export const ENABLE_ANALYTICS = currentConfig.ENABLE_ANALYTICS;

// Third-party Keys
export const RECAPTCHA_SITE_KEY = currentConfig.RECAPTCHA_SITE_KEY;


// ==========================================================================
// API HELPERS
// ==========================================================================

/**
 * Build full API URL with endpoint
 * @param {string} endpoint - API endpoint (e.g., '/ai/chat')
 * @returns {string} Full URL
 */
export const buildApiUrl = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_URL}${cleanEndpoint}`;
};

/**
 * Get default headers for API requests
 * @param {boolean} includeAuth - Whether to include API key
 * @returns {Object} Headers object
 */
export const getApiHeaders = (includeAuth = true) => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (includeAuth && API_KEY) {
    headers['x-api-key'] = API_KEY;
  }
  
  return headers;
};

/**
 * Build headers with user authentication token
 * @param {string} token - User's auth token
 * @returns {Object} Headers object
 */
export const getAuthHeaders = (token) => {
  return {
    ...getApiHeaders(),
    'Authorization': `Bearer ${token}`,
  };
};


// ==========================================================================
// DEBUG LOGGING
// ==========================================================================

/**
 * Conditional console.log that only runs in development
 * @param  {...any} args - Arguments to log
 */
export const devLog = (...args) => {
  if (ENABLE_DEBUG_LOGGING) {
    console.log('[IAMONEAI]', ...args);
  }
};

/**
 * Conditional console.warn that only runs in development
 * @param  {...any} args - Arguments to log
 */
export const devWarn = (...args) => {
  if (ENABLE_DEBUG_LOGGING) {
    console.warn('[IAMONEAI]', ...args);
  }
};

/**
 * Conditional console.error (always logs, but with more detail in dev)
 * @param  {...any} args - Arguments to log
 */
export const devError = (...args) => {
  if (ENABLE_DEBUG_LOGGING) {
    console.error('[IAMONEAI ERROR]', ...args);
  } else {
    // In production, log minimal info
    console.error('[IAMONEAI]', args[0]);
  }
};

/**
 * Log API request (dev only)
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {Object} body - Request body
 */
export const logApiRequest = (method, url, body) => {
  if (ENABLE_DEBUG_LOGGING) {
    console.groupCollapsed(`[API] ${method} ${url}`);
    console.log('Body:', body);
    console.groupEnd();
  }
};

/**
 * Log API response (dev only)
 * @param {string} url - Request URL
 * @param {number} status - HTTP status code
 * @param {Object} data - Response data
 */
export const logApiResponse = (url, status, data) => {
  if (ENABLE_DEBUG_LOGGING) {
    const icon = status >= 200 && status < 300 ? '✅' : '❌';
    console.groupCollapsed(`[API] ${icon} ${status} ${url}`);
    console.log('Response:', data);
    console.groupEnd();
  }
};


// ==========================================================================
// VERSION INFO
// ==========================================================================

export const APP_VERSION = '1.0.0';
export const BUILD_DATE = new Date().toISOString().split('T')[0];


// ==========================================================================
// TIMEOUTS & LIMITS
// ==========================================================================

export const REQUEST_TIMEOUT_MS = 30000; // 30 seconds
export const LONG_REQUEST_TIMEOUT_MS = 60000; // 60 seconds for memory operations
export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_DELAY_MS = 1000;


// ==========================================================================
// EXTERNAL LINKS
// ==========================================================================

export const EXTERNAL_LINKS = {
  LANDING_PAGE: 'https://iamoneai.com',
  DOCUMENTATION: 'https://docs.iamoneai.com',
  PRIVACY_POLICY: 'https://iamoneai.com/privacy',
  TERMS_OF_SERVICE: 'https://iamoneai.com/terms',
  SUPPORT: 'https://iamoneai.com/support',
  TWITTER: 'https://twitter.com/iamoneai',
  GITHUB: 'https://github.com/iamoneai',
};


// ==========================================================================
// FEATURE FLAGS (for gradual rollout)
// ==========================================================================

export const FEATURES = {
  // Auth features
  REGISTRATION_ENABLED: false, // Coming soon
  WAITLIST_ENABLED: true,
  SOCIAL_LOGIN_ENABLED: false,
  
  // Chat features
  VOICE_INPUT_ENABLED: false,
  FILE_UPLOAD_ENABLED: false,
  
  // Memory features
  MEMORY_EXPORT_ENABLED: false,
  MEMORY_IMPORT_ENABLED: false,
  
  // LLM features
  BYOK_ENABLED: false, // Bring Your Own Key
  MULTI_LLM_ENABLED: false,
  
  // UI features
  ONBOARDING_FLOW_ENABLED: true,
  ANALYTICS_DASHBOARD_ENABLED: false,
  GOAL_MANAGEMENT_ENABLED: true,
  
  // Experimental
  PROACTIVE_CHECKINS_ENABLED: false,
  MEMORY_COMPRESSION_ENABLED: false,
};


// ==========================================================================
// VALIDATION
// ==========================================================================

/**
 * Validate that required environment variables are present
 * Call this on app startup
 */
export const validateEnvironment = () => {
  const required = ['API_URL', 'API_KEY'];
  const missing = [];
  
  if (!API_URL) missing.push('API_URL');
  if (!API_KEY) missing.push('API_KEY');
  
  if (missing.length > 0) {
    console.error(`Missing required environment configuration: ${missing.join(', ')}`);
    return false;
  }
  
  devLog('Environment validated:', {
    environment: ENVIRONMENT,
    apiUrl: API_URL,
    appUrl: APP_URL,
  });
  
  return true;
};


// ==========================================================================
// DEFAULT EXPORT
// ==========================================================================

export default {
  // Environment
  ENVIRONMENT,
  IS_DEVELOPMENT,
  IS_STAGING,
  IS_PRODUCTION,
  
  // API
  API_URL,
  API_KEY,
  APP_URL,
  buildApiUrl,
  getApiHeaders,
  getAuthHeaders,
  
  // Feature flags
  ENABLE_DEBUG_LOGGING,
  ENABLE_MOCK_DATA,
  ENABLE_DEV_TOOLS,
  ENABLE_ANALYTICS,
  FEATURES,
  
  // Third-party
  RECAPTCHA_SITE_KEY,
  
  // Logging
  devLog,
  devWarn,
  devError,
  logApiRequest,
  logApiResponse,
  
  // Version
  APP_VERSION,
  BUILD_DATE,
  
  // Timeouts
  REQUEST_TIMEOUT_MS,
  LONG_REQUEST_TIMEOUT_MS,
  MAX_RETRY_ATTEMPTS,
  RETRY_DELAY_MS,
  
  // Links
  EXTERNAL_LINKS,
  
  // Validation
  validateEnvironment,
};